import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/useApp';
import { useAuth } from '../context/useAuth';
import { useOrganizations } from '../context/useOrganizations';
import { useLanguage } from '../i18n';
import { sectionOrder, getSectionLabels, sectionPrompts, type SectionKey } from '../prompts';
import { generateContent, GenerationError } from '../services/ai';
import { downloadMarkdown, downloadSection } from '../utils/export';
import DocumentViewer from './DocumentViewer';
import UpgradePrompt from './UpgradePrompt';
import { Sparkles, Download, RefreshCw, AlertCircle, Settings, ArrowLeft, Lock, MessageCircleQuestion, Building2, ExternalLink, X, Plus } from 'lucide-react';

interface ProjectWorkspaceProps {
  onOpenSettings: () => void;
  onOpenDashboard: () => void;
  onOpenUpgrade?: () => void;
  onOpenAdvance?: () => void;
  onOpenOrganization: (orgId: string) => void;
}

type WorkspaceTab = SectionKey | 'linkedOrgs';

const gatedSections: SectionKey[] = [
  'sops',
  'legalCompliance',
  'orgChartRoles',
  'employeeOutlines',
  'scalingFramework',
];

function isGated(key: SectionKey, plan: string | undefined): boolean {
  if (!plan || plan === 'starter') return gatedSections.includes(key);
  return false;
}

export default function ProjectWorkspace({ onOpenSettings, onOpenDashboard, onOpenUpgrade, onOpenAdvance, onOpenOrganization }: ProjectWorkspaceProps) {
  const { activeProjectId, projects, updateSection, setActiveProjectId } = useApp();
  const { organizations, linkToProject, unlinkFromProject } = useOrganizations();
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const p = t.project;
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(sectionOrder[0]);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState<null | { variant: 'limit' | 'locked' | 'blocked'; sections?: string }>(null);
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);

  useEffect(() => {
    setUpgradeMode(null);
  }, [activeTab, user?.plan]);

  const project = projects.find((p) => p.id === activeProjectId);
  const projectLocale = project?.input.locale || locale;
  const sectionLabels = getSectionLabels(projectLocale);

  const linkedOrgs = useMemo(() => {
    if (!project) return [];
    const ids = new Set(
      organizations.filter((o) => (o.linkedProjectIds || []).includes(project.id)).map((o) => o.id)
    );
    return organizations.filter((o) => ids.has(o.id));
  }, [organizations, project]);
  const unlinkedOrgs = useMemo(() => {
    if (!project) return [];
    const linkedIds = new Set(linkedOrgs.map((o) => o.id));
    return organizations.filter((o) => !linkedIds.has(o.id));
  }, [organizations, linkedOrgs, project]);

  if (!project) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{p.errorTitle}</h2>
          <p className="text-slate mb-4">{t.dashboard.emptyDescription}</p>
          <button
            onClick={() => setActiveProjectId(null)}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium"
          >
            {p.back}
          </button>
        </div>
      </div>
    );
  }

  const activeSection = activeTab === 'linkedOrgs' ? null : project.sections[activeTab];

  const generateSection = async (key: SectionKey, allowStop = false): Promise<boolean> => {
    const targetContent = project.sections[key].content;
    if (isGated(key, user?.plan)) {
      setUpgradeMode({ variant: 'locked', sections: sectionLabels[key] });
      return false;
    }
    setUpgradeMode(null);
    void updateSection(project.id, key, targetContent, 'loading');
    try {
      const promptFn = sectionPrompts[key];
      const prompt = promptFn(project.input, projectLocale);
      const result = await generateContent({
        section: key,
        prompt,
        locale: projectLocale,
      });
      void updateSection(project.id, key, result.content, 'done');
      return true;
    } catch (err) {
      if (err instanceof GenerationError && (err.code === 'PLAN_LIMIT' || err.code === 'UPGRADE_REQUIRED')) {
        setUpgradeMode({ variant: err.code === 'PLAN_LIMIT' ? 'limit' : 'locked', sections: sectionLabels[key] });
        void updateSection(project.id, key, targetContent, 'idle');
        return false;
      }
      const message = err instanceof Error ? err.message : p.errorTitle;
      void updateSection(project.id, key, targetContent, 'error', message);
      return allowStop ? false : true;
    }
  };

  const generateAll = async () => {
    const blocked = sectionOrder.filter((key) => isGated(key, user?.plan));
    if (blocked.length > 0) {
      setUpgradeMode({ variant: 'blocked', sections: blocked.map((k) => sectionLabels[k]).join(', ') });
      return;
    }
    setUpgradeMode(null);
    setGeneratingAll(true);
    for (const key of sectionOrder) {
      const ok = await generateSection(key, true);
      if (!ok) break;
    }
    setGeneratingAll(false);
  };

  const regenerateCurrent = () => {
    if (activeTab !== 'linkedOrgs') generateSection(activeTab);
  };

  return (
    <div className="flex h-full flex-col">
      <header className="bg-white border-b border-cloud px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={onOpenDashboard}
              className="p-2 -ml-1 text-ash hover:text-ink hover:bg-mist transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Back to projects"
              aria-label="Back to projects"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-ink truncate">{project.name}</h1>
              <p className="text-xs sm:text-sm text-slate truncate">
                {project.input.industry || t.dashboard.industry} · {project.input.location || '—'} · {project.input.stage || '—'}
                {' '}
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-mist text-slate border border-cloud">
                  {projectLocale === 'de' ? 'DE' : 'EN'}
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto">
            {user && (
              <span className="hidden md:inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
                {t.plans[user.plan]} · {user.generations_used}/{user.generations_limit}
              </span>
            )}
            <button
              onClick={onOpenSettings}
              className="p-2 text-slate hover:bg-mist transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={t.nav.settings}
              aria-label={t.nav.settings}
            >
              <Settings size={18} />
            </button>
            <button
              onClick={() => downloadMarkdown(project)}
              className="flex-1 sm:flex-none min-h-[44px] px-3 py-2 text-ink hover:bg-mist font-medium flex items-center justify-center gap-2 border border-cloud transition-colors"
            >
              <Download size={16} /> <span className="sm:inline">{p.export}</span>
            </button>
            <button
              onClick={generateAll}
              disabled={generatingAll}
              className="flex-1 sm:flex-none min-h-[44px] px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Sparkles size={18} />
              {generatingAll ? p.generating : p.generateAll}
            </button>
          </div>
        </div>
      </header>

      {/* Section rail — desktop: w-64 left rail; mobile: horizontal scrollable tablist. */}
      <nav className="hidden md:block w-64 bg-white border-r border-cloud overflow-y-auto shrink-0">
        {sectionOrder.map((key) => {
          const section = project.sections[key];
          const locked = isGated(key, user?.plan);
          const statusIcon =
            section.status === 'done' ? '✓' : section.status === 'loading' ? '⏳' : section.status === 'error' ? '!' : '○';
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`w-full text-left px-4 py-3 border-b border-mist flex items-center justify-between transition-colors ${
                activeTab === key
                  ? 'bg-red-50 border-l-4 border-l-red-500'
                  : 'hover:bg-mist border-l-4 border-l-transparent'
              }`}
            >
              <span className={`font-medium ${activeTab === key ? 'text-ink' : 'text-slate'}`}>
                {sectionLabels[key]}
              </span>
              <span className="flex items-center gap-1.5">
                {locked && <Lock size={12} className="text-ash" />}
                <span
                  className={`text-xs ${
                    section.status === 'done'
                      ? 'text-emerald-600'
                      : section.status === 'error'
                      ? 'text-red-500'
                      : section.status === 'loading'
                      ? 'text-amber-500'
                      : 'text-ash'
                  }`}
                >
                  {statusIcon}
                </span>
              </span>
            </button>
          );
        })}

        {onOpenAdvance && (
          <>
            <div className="border-t border-mist mt-4 pt-4 px-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-ash">Interview</p>
            </div>
            <button
              type="button"
              onClick={onOpenAdvance}
              className="w-full text-left px-4 py-3 border-b border-mist flex items-center justify-between transition-colors hover:bg-red-50 border-l-4 border-l-transparent hover:border-l-red-500"
            >
              <span className="font-medium text-slate flex items-center gap-2">
                <MessageCircleQuestion size={16} />
                {t.interview.advanceTitle}
              </span>
            </button>
          </>
        )}

        <div className="border-t border-mist mt-4 pt-4 px-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-ash">{p.linkedOrgsRail}</p>
        </div>
        <button
          type="button"
          onClick={() => setActiveTab('linkedOrgs')}
          className={`w-full text-left px-4 py-3 border-b border-mist flex items-center justify-between transition-colors ${
            activeTab === 'linkedOrgs'
              ? 'bg-red-50 border-l-4 border-l-red-500'
              : 'hover:bg-mist border-l-4 border-l-transparent'
          }`}
        >
          <span className={`font-medium flex items-center gap-2 ${activeTab === 'linkedOrgs' ? 'text-ink' : 'text-slate'}`}>
            <Building2 size={16} />
            {p.linkedOrgsRail}
          </span>
          <span className="text-xs text-slate">{linkedOrgs.length}</span>
        </button>
      </nav>

      {/* Mobile section tabs */}
      <div className="md:hidden bg-white border-b border-cloud overflow-x-auto shrink-0">
        <div role="tablist" className="flex w-max min-w-full">
          {sectionOrder.map((key) => {
            const section = project.sections[key];
            const locked = isGated(key, user?.plan);
            const statusIcon =
              section.status === 'done' ? '✓' : section.status === 'loading' ? '⏳' : section.status === 'error' ? '!' : '○';
            const active = activeTab === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3 min-h-[48px] text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  active
                    ? 'text-red-600 border-red-500'
                    : 'text-slate border-transparent hover:text-ink hover:bg-mist'
                }`}
              >
                {locked && <Lock size={12} className="text-ash" />}
                <span className="truncate max-w-[10rem]">{sectionLabels[key]}</span>
                <span
                  className={`text-xs ${
                    section.status === 'done'
                      ? 'text-emerald-600'
                      : section.status === 'error'
                      ? 'text-red-500'
                      : section.status === 'loading'
                      ? 'text-amber-500'
                      : 'text-ash'
                  }`}
                >
                  {statusIcon}
                </span>
              </button>
            );
          })}
          {onOpenAdvance && (
            <button
              type="button"
              onClick={onOpenAdvance}
              className="flex items-center gap-1.5 px-3 min-h-[48px] text-sm font-medium border-b-2 border-transparent text-slate hover:text-red-600 hover:bg-mist whitespace-nowrap"
            >
              <MessageCircleQuestion size={14} />
              {t.interview.advanceTitle}
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'linkedOrgs'}
            onClick={() => setActiveTab('linkedOrgs')}
            className={`flex items-center gap-1.5 px-3 min-h-[48px] text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'linkedOrgs'
                ? 'text-red-600 border-red-500'
                : 'text-slate border-transparent hover:text-ink hover:bg-mist'
            }`}
          >
            <Building2 size={14} />
            {p.linkedOrgsRail}
            <span className="text-xs text-slate">{linkedOrgs.length}</span>
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <section className="flex-1 overflow-y-auto bg-paper p-4 sm:p-6">
          <div className="max-w-5xl mx-auto">
            {activeTab === 'linkedOrgs' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-ink truncate">{p.linkedOrgsTitle}</h2>
                    <p className="text-sm text-slate mt-1 hidden sm:block">{t.document.outline}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOrgPickerOpen((v) => !v)}
                    className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 border border-red-100 transition-colors"
                  >
                    {orgPickerOpen ? <X size={14} /> : <Plus size={14} />}
                    {p.addOrgCta}
                  </button>
                </div>

                {orgPickerOpen && (
                  <div className="bg-white border border-cloud p-4">
                    {unlinkedOrgs.length === 0 ? (
                      <p className="text-sm text-ash italic">{p.addOrgEmpty}</p>
                    ) : (
                      <ul className="divide-y divide-mist">
                        {unlinkedOrgs.map((org) => (
                          <li key={org.id} className="py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0 flex items-center gap-2">
                              <Building2 size={14} className="text-slate shrink-0" />
                              <span className="font-medium text-ink truncate">{org.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                void linkToProject(org.id, project.id);
                              }}
                              className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-100 flex items-center gap-1.5 transition-colors shrink-0"
                            >
                              <Plus size={14} /> {p.addOrgCta}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="bg-white border border-cloud p-5 sm:p-6">
                  {linkedOrgs.length === 0 ? (
                    <p className="text-sm text-ash italic">{p.noLinkedOrgs}</p>
                  ) : (
                    <ul className="divide-y divide-mist">
                      {linkedOrgs.map((org) => (
                        <li key={org.id} className="py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex items-center gap-2">
                            <Building2 size={16} className="text-red-500 shrink-0" />
                            <div className="min-w-0">
                              <div className="font-medium text-ink truncate">{org.name}</div>
                              <div className="text-xs text-slate truncate">{org.idea || '—'}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => onOpenOrganization(org.id)}
                              className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-100 flex items-center gap-1.5 transition-colors"
                            >
                              <ExternalLink size={14} /> {p.openOrg}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm(p.unlinkOrgConfirm(org.name))) {
                                  void unlinkFromProject(org.id, project.id);
                                }
                              }}
                              className="min-h-[44px] px-3 py-2 text-sm font-medium text-ash hover:bg-mist border border-cloud flex items-center gap-1.5 transition-colors"
                              aria-label={`${p.unlinkOrg} ${org.name}`}
                            >
                              <X size={14} /> {p.unlinkOrg}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : activeSection ? (
              <>
                <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-ink truncate">{sectionLabels[activeTab]}</h2>
                    <p className="text-sm text-slate mt-1 hidden sm:block">{t.document.outline}</p>
                  </div>
                  <button
                    onClick={regenerateCurrent}
                    disabled={activeSection.status === 'loading' || isGated(activeTab, user?.plan)}
                    className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 border border-red-100 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <RefreshCw size={14} className={activeSection.status === 'loading' ? 'animate-spin' : ''} />
                    {activeSection.status === 'loading' ? p.generating : p.regenerate}
                  </button>
                </div>

                {activeSection.status === 'error' && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-100 flex items-start gap-3">
                    <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="font-medium text-red-800">{p.errorTitle}</p>
                      <p className="text-sm text-red-700">{activeSection.error}</p>
                      <p className="text-sm text-red-700 mt-2">{p.errorSupport}</p>
                    </div>
                  </div>
                )}

                {upgradeMode && onOpenUpgrade && (
                  <UpgradePrompt
                    variant={upgradeMode.variant}
                    sectionName={upgradeMode.sections}
                    onUpgrade={onOpenUpgrade}
                  />
                )}

                {activeSection.status === 'idle' && (
                  <div className="bg-white border border-cloud p-6 sm:p-8 text-center">
                    <Sparkles size={32} className="text-red-400 mx-auto mb-3" />
                    <h3 className="font-semibold text-lg mb-2">{p.idleTitle(sectionLabels[activeTab])}</h3>
                    <p className="text-slate mb-4 max-w-md mx-auto">{p.idleDescription}</p>
                    {isGated(activeTab, user?.plan) && onOpenUpgrade ? (
                      <UpgradePrompt
                        variant="locked"
                        sectionName={sectionLabels[activeTab]}
                        onUpgrade={onOpenUpgrade}
                      />
                    ) : (
                      <button
                        onClick={regenerateCurrent}
                        className="min-h-[44px] px-5 py-2 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
                      >
                        {p.idleCta}
                      </button>
                    )}
                  </div>
                )}

                {(activeSection.status === 'done' || activeSection.status === 'loading') && (
                  <DocumentViewer
                    content={activeSection.content}
                    projectName={project.name}
                    projectId={project.id}
                    sectionKey={activeTab}
                    locale={projectLocale}
                    loading={activeSection.status === 'loading'}
                    onExportSection={(section) => downloadSection(project.name, section)}
                  />
                )}
              </>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
